import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const AdvancedSettingsTab = () => {
  const [advancedSettings, setAdvancedSettings] = useState({
    helpDeskIntegration: true,
    helpDeskUrl: 'https://soporte.vinpro.mx',
    systemStatusMonitoring: true,
    alertThresholds: {
      responseTime: 2000, // milliseconds
      errorRate: 5, // percentage
      cpuUsage: 80, // percentage
      memoryUsage: 85 // percentage
    },
    maintenanceMode: false,
    maintenanceMessage: 'Sistema en mantenimiento programado. Regresaremos pronto.',
    backupRetention: 30, // days
    logLevel: 'info',
    apiRateLimit: 1000, // requests per hour
    cacheExpiration: 3600 // seconds
  });

  const [hasChanges, setHasChanges] = useState(false);

  const logLevelOptions = [
    { value: 'error', label: 'Error - Solo errores críticos' },
    { value: 'warn', label: 'Warning - Errores y advertencias' },
    { value: 'info', label: 'Info - Información general' },
    { value: 'debug', label: 'Debug - Información detallada' },
    { value: 'trace', label: 'Trace - Información completa' }
  ];

  const handleSettingChange = (key, value) => {
    if (key?.includes('.')) {
      const [parent, child] = key?.split('.');
      setAdvancedSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setAdvancedSettings(prev => ({
        ...prev,
        [key]: value
      }));
    }
    setHasChanges(true);
  };

  const handleSave = () => {
    console.log('Guardando configuración avanzada:', advancedSettings);
    setHasChanges(false);
  };

  const handleReset = () => {
    setAdvancedSettings({
      helpDeskIntegration: true,
      helpDeskUrl: 'https://soporte.vinpro.mx',
      systemStatusMonitoring: true,
      alertThresholds: {
        responseTime: 2000,
        errorRate: 5,
        cpuUsage: 80,
        memoryUsage: 85
      },
      maintenanceMode: false,
      maintenanceMessage: 'Sistema en mantenimiento programado. Regresaremos pronto.',
      backupRetention: 30,
      logLevel: 'info',
      apiRateLimit: 1000,
      cacheExpiration: 3600
    });
    setHasChanges(false);
  };

  const testHelpDeskConnection = () => {
    // Mock test
    alert('Conexión con help desk exitosa');
  };

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Settings" size={20} className="text-purple-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-purple-900">Configuración Avanzada</h4>
            <p className="text-sm text-purple-700 mt-1">
              Configuraciones técnicas del sistema, monitoreo y integración con servicios externos. Requiere conocimientos técnicos avanzados.
            </p>
          </div>
        </div>
      </div>
      {/* Help Desk Integration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground">Integración Help Desk</h3>
            <p className="text-sm text-muted-foreground">
              Configurar integración con sistema de soporte técnico
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {advancedSettings?.helpDeskIntegration ? 'Activado' : 'Desactivado'}
            </span>
            <button
              onClick={() => handleSettingChange('helpDeskIntegration', !advancedSettings?.helpDeskIntegration)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                advancedSettings?.helpDeskIntegration ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  advancedSettings?.helpDeskIntegration ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {advancedSettings?.helpDeskIntegration && (
          <div className="space-y-4">
            <div className="flex space-x-3">
              <div className="flex-1">
                <Input
                  label="URL del Help Desk"
                  type="url"
                  value={advancedSettings?.helpDeskUrl}
                  onChange={(e) => handleSettingChange('helpDeskUrl', e?.target?.value)}
                  placeholder="https://soporte.vinpro.mx"
                  description="URL del sistema de tickets de soporte"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={testHelpDeskConnection}
                  iconName="TestTube"
                  iconPosition="left"
                >
                  Probar Conexión
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* System Monitoring */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground">Monitoreo del Sistema</h3>
            <p className="text-sm text-muted-foreground">
              Configurar alertas y umbrales de rendimiento
            </p>
          </div>
          <button
            onClick={() => handleSettingChange('systemStatusMonitoring', !advancedSettings?.systemStatusMonitoring)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              advancedSettings?.systemStatusMonitoring ? 'bg-primary' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                advancedSettings?.systemStatusMonitoring ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {advancedSettings?.systemStatusMonitoring && (
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Tiempo de Respuesta Máximo (ms)"
              type="number"
              value={advancedSettings?.alertThresholds?.responseTime}
              onChange={(e) => handleSettingChange('alertThresholds.responseTime', parseInt(e?.target?.value))}
              min="500"
              max="10000"
              description="Alerta si el tiempo de respuesta excede este valor"
            />

            <Input
              label="Tasa de Error Máxima (%)"
              type="number"
              value={advancedSettings?.alertThresholds?.errorRate}
              onChange={(e) => handleSettingChange('alertThresholds.errorRate', parseInt(e?.target?.value))}
              min="1"
              max="50"
              description="Alerta si la tasa de error supera este porcentaje"
            />

            <Input
              label="Uso de CPU Máximo (%)"
              type="number"
              value={advancedSettings?.alertThresholds?.cpuUsage}
              onChange={(e) => handleSettingChange('alertThresholds.cpuUsage', parseInt(e?.target?.value))}
              min="50"
              max="95"
              description="Alerta si el uso de CPU supera este porcentaje"
            />

            <Input
              label="Uso de Memoria Máximo (%)"
              type="number"
              value={advancedSettings?.alertThresholds?.memoryUsage}
              onChange={(e) => handleSettingChange('alertThresholds.memoryUsage', parseInt(e?.target?.value))}
              min="50"
              max="95"
              description="Alerta si el uso de memoria supera este porcentaje"
            />
          </div>
        )}
      </div>
      {/* Maintenance Mode */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground">Modo de Mantenimiento</h3>
            <p className="text-sm text-muted-foreground">
              Activar modo de mantenimiento para actualizaciones del sistema
            </p>
          </div>
          <button
            onClick={() => handleSettingChange('maintenanceMode', !advancedSettings?.maintenanceMode)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              advancedSettings?.maintenanceMode ? 'bg-red-500' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                advancedSettings?.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {advancedSettings?.maintenanceMode && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-red-600" />
                <span className="text-sm text-red-800 font-medium">
                  ¡ADVERTENCIA! El modo de mantenimiento bloqueará el acceso a todos los usuarios.
                </span>
              </div>
            </div>

            <Input
              label="Mensaje de Mantenimiento"
              type="text"
              value={advancedSettings?.maintenanceMessage}
              onChange={(e) => handleSettingChange('maintenanceMessage', e?.target?.value)}
              description="Mensaje que verán los usuarios durante el mantenimiento"
            />
          </div>
        )}
      </div>
      {/* System Performance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Rendimiento del Sistema</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Límite de Solicitudes API (por hora)"
            type="number"
            value={advancedSettings?.apiRateLimit}
            onChange={(e) => handleSettingChange('apiRateLimit', parseInt(e?.target?.value))}
            min="100"
            max="10000"
            description="Número máximo de solicitudes API por usuario por hora"
          />

          <Input
            label="Expiración de Caché (segundos)"
            type="number"
            value={advancedSettings?.cacheExpiration}
            onChange={(e) => handleSettingChange('cacheExpiration', parseInt(e?.target?.value))}
            min="300"
            max="86400"
            description="Tiempo de vida de los datos en caché"
          />

          <Input
            label="Retención de Respaldos (días)"
            type="number"
            value={advancedSettings?.backupRetention}
            onChange={(e) => handleSettingChange('backupRetention', parseInt(e?.target?.value))}
            min="7"
            max="365"
            description="Días para conservar respaldos automáticos"
          />

          <Select
            label="Nivel de Logging"
            description="Detalle de información en los logs del sistema"
            options={logLevelOptions}
            value={advancedSettings?.logLevel}
            onChange={(value) => handleSettingChange('logLevel', value)}
          />
        </div>
      </div>
      {/* System Status */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={16} className="text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Tiempo Respuesta</h4>
            </div>
          </div>
          <div className="text-xl font-bold text-foreground">245ms</div>
          <div className="text-xs text-muted-foreground">promedio últimas 24h</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Cpu" size={16} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">CPU</h4>
            </div>
          </div>
          <div className="text-xl font-bold text-foreground">42%</div>
          <div className="text-xs text-muted-foreground">uso actual</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="HardDrive" size={16} className="text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Memoria</h4>
            </div>
          </div>
          <div className="text-xl font-bold text-foreground">67%</div>
          <div className="text-xs text-muted-foreground">uso actual</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Errores</h4>
            </div>
          </div>
          <div className="text-xl font-bold text-foreground">0.2%</div>
          <div className="text-xs text-muted-foreground">tasa últimas 24h</div>
        </div>
      </div>
      {hasChanges && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Tienes cambios sin guardar en la configuración avanzada
            </span>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Descartar
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              Guardar Cambios
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettingsTab;